<template>
  <b-tabs v-model="activeTabIndex">
    <b-tab title="Check-In">
      <checkin-card
        :key="kr.id"
        :kr="kr"
        :org-id="kr.organizationId"
        :is-collapsed="isCheckinCollapsed"
        @post-checkin="$emit('post-checkin')"
      />
    </b-tab>
    <b-tab title="Comment" @click="onCommentTabFocus">
      <create-comment
        class="m-n3"
        :mentionable-users="usersInScope"
        ref="commentEditor"
        :errors="commentErrors"
        :org-id="kr.organizationId"
        :focus-add-comment-on-load="true"
        :is-saving="isCreatingComment"
        :entity-type="ENTITY_TYPE.KEY_RESULT"
        :entity="kr"
        :show-subscribers="true"
        theme="light"
        form-boundary="editor"
        @save="postNewComment"
        @reset-errors="commentErrors = null"
      />
    </b-tab>
  </b-tabs>
</template>

<script>
import MembershipsMixin from '@/mixins/MembershipsMixin'
import replyAsComment from '@/utils/replyAsComment'
import * as R from 'ramda'
import CheckinCard from '@/components/kr/CheckinCard'
import CreateComment from '@/components/CreateComment'
import { MIME_TYPE } from '@/constants/common'
import EventBus from '@/EventBus'
import { BTab, BTabs } from 'bootstrap-vue'
import EventBusMixin from '@/mixins/EventBusMixin'
import events from '@/constants/events'
import { ENTITY_TYPE } from '@/constants/entities'
import pickError from '@/utils/pickError'

/**
 * This component gives a tab based widget, which lets user checkin & comment on a KR
 @emits:
 * - post-checkin: emitted when checkin is posted
 * - commented: emitted when comment is posted
 */
export default {
  name: 'PostKrUpdates',
  props: {
    kr: Object,
    isCheckinCollapsed: Boolean
  },
  components: {
    BTabs,
    BTab,
    CheckinCard,
    CreateComment
  },
  data() {
    const tabs = ['checkin', 'comment']
    return {
      commentErrors: null,
      isCreatingComment: false,
      ENTITY_TYPE,
      tabs,
      activeTabIndex: 0
    }
  },
  mixins: [EventBusMixin, MembershipsMixin],
  computed: {
    goal() {
      return this.$store.getters.goalById(this.kr.goalId)
    },
    team() {
      const teamId = this.goal.teamId
      return teamId && this.$store.getters.teamById(teamId)
    },
    usersInScope() {
      return this.getUsersInScope(this.goal.teamId, this.goal.organizationId)
    }
  },
  methods: {
    async postNewComment(comment) {
      try {
        this.isCreatingComment = true
        const response = await this.$store.dispatch('createKrEvent', {
          orgId: this.goal.organizationId,
          goalId: this.goal.id,
          krId: this.kr.id,
          event: {
            comment,
            commentType: MIME_TYPE.HTML,
            keyResultId: this.kr.id
          }
        })
        EventBus.$emit('ADD_TOAST', { message: 'Comment has been posted successfully' })
        this.$refs.commentEditor.$refs.editor.setData(null)
        this.commentErrors = null
        this.eventBusEmit(events.KR_COMMENT_ADDED, response.data)
      } catch (e) {
        this.commentErrors = R.pathOr(pickError(e), ['response', 'data', 'detail', 'comment'], e)
      }
      this.isCreatingComment = false
      this.$emit('commented')
    },
    onCommentTabFocus() {
      setTimeout(() => this.$refs.commentEditor.$refs.editor?.focus(), 100)
    },
    /**
     * This is getting used in parent components
     */
    triggerReply(event) {
      this.activeTabIndex = this.tabs.indexOf('comment')
      this.$nextTick(() => {
        replyAsComment(event, this.$store, this.$refs.commentEditor)
      })
    }
  },
  mounted() {}
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';

.tabs /deep/ {
  .nav-link.active {
    border-color: $sky $sky $selectionColor;
  }

  .tab-content {
    border: 1px solid $sky;
    padding: 16px 40px;
    border-radius: 10px;
  }

  .nav-link.active,
  .tab-content {
    background-color: $selectionColor;
  }

  .nav-tabs {
    border-bottom: none;
    padding-left: 40px;
  }
}
@include media-breakpoint-down(md) {
  .tabs /deep/ .tab-content {
    padding: 16px 15px;
  }
  .tabs {
    margin-left: 0;
  }
}
</style>
